import {inject, Injectable} from '@angular/core';
import {
  Defect, FlatRateUnit, IDefect, IFlatRateUnit, IPackage, IPart, ITechnicalDocument, Package, Part, TechnicalDocument
} from '@service-and-repairs/awpintegrationlib';
import {CustomerLibConfigurable} from '@service-and-repairs/calm-leads-customer-lib-api';
import {DmsService} from '../components/dms-search/dms.service';
import {CustomerService} from '../core/customer/services/customer.service';
import {MileageLoader} from '../core/mileage/services/mileage.loader';
import {ServiceCaseHolder} from '../core/service-case/models/service-case-holder';
import {ServiceCaseManager} from '../core/service-case/models/service-case-manager';
import {ServiceCaseUpdater} from '../core/service-case/models/service-case-updater';
import {IWebComponentAppConfiguration} from '../interfaces/IWebComponentAppConfiguration';
import {
  FlatRateUnitSearchAddEvent,
  FlatRateUnitSearchFlatRateGroup,
  FlatRateUnitSearchFlatRateUnit,
  PairSearchAddEvent,
  PairSearchDefect,
  PairSearchFlatRateUnit,
  PairSearchPackage,
  PairSearchPart,
  PairSearchTechnicalDocument,
  PartsCatalogAddEvent,
  PartsCatalogPart
} from './types';

/**
 * This service provides event listeners to react to web component app events (outputs).
 */
@Injectable({
  providedIn: 'root'
})
export class WebComponentEventService {
  private readonly dmsService: DmsService = inject(DmsService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly serviceCaseUpdater: ServiceCaseUpdater = inject(ServiceCaseUpdater);
  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly customerService: CustomerService = inject(CustomerService);
  private readonly mileageLoader: MileageLoader = inject(MileageLoader);

  getEventListenersForApp(app: IWebComponentAppConfiguration): object {
    if (app.routingPath === 'flatratesearch') {
      return {
        addToList: (event: CustomEvent<FlatRateUnitSearchAddEvent>): void => {
          const flatRateUnits: IFlatRateUnit[] = event.detail.flatrateGroups.flatMap((group: FlatRateUnitSearchFlatRateGroup): IFlatRateUnit[] => group.flatrates.map(
            (fru: FlatRateUnitSearchFlatRateUnit): IFlatRateUnit => this.mapFlatRateUnitFromFlatRateUnitSearch(fru)));
          this.serviceCaseUpdater.addFlatRateUnits(flatRateUnits, 'flatratesearch');
        }
      };
    } else if (app.routingPath === 'servicedemand' || app.routingPath === 'serviceleads' || app.routingPath === 'multiserviceleads') {
      return {
        ready: (event: Event): void => {
          // set Customer Lib on leads components once they are ready
          this.customerService.customerLibLoaded
            .then((customerLib: CustomerLibConfigurable): void => event.target['api'].setCustomerLibInstance?.(
              customerLib));
        },
        requestMileageUpdate: (): void => {
          this.mileageLoader.loadMileage(this.serviceCaseHolder.getActiveCase())
            .then((mustSave: boolean): void => this.serviceCaseManager.onServiceCaseUpdated(
              this.serviceCaseHolder.getActiveCase(),
              mustSave,
              'mileage loader'
            ));
        }
      };
    } else if (app.routingPath === 'partssearch') {
      return {
        // set dmsApi on parts search once it is ready
        ready: (event: Event): void => event.target['api']?.setDmsInstance?.(this.dmsService.dmsApi),
        addPartsEvent: (event: CustomEvent<PartsCatalogAddEvent>): void => {
          const parts: IPart[] = [this.mapPartFromPartsCatalog(event.detail.part)]
            .concat(event.detail.additionalParts.map((part: PartsCatalogPart): IPart => this.mapPartFromPartsCatalog(
              part)));
          this.serviceCaseUpdater.addParts(parts, 'partssearch');
        }
      };
    } else if (app.routingPath === 'caseoverview') {
      return {
        metaDataUpdated: (event: CustomEvent): void => {
          const serviceCaseId: string = event.detail;
          if (this.serviceCaseHolder.isServiceCaseOpen(serviceCaseId)) {
            this.serviceCaseManager.reloadServiceCaseById(serviceCaseId, false);
          }
        }
      };
    } else if (app.routingPath === 'casedetails') {
      return {
        ready: (event: Event): void => {
          event.target['api']?.setDmsInstance?.(this.dmsService.dmsApi);
          this.customerService.customerLibLoaded
            .then((customerLib: CustomerLibConfigurable): void => event.target['api']?.setCustomerLibInstance?.(
              customerLib));
        }
      };
    } else if (app.routingPath === 'partsorderlist' || app.routingPath === 'dealerchecklist') {
      return {
        ready: (event: Event): void => {
          event.target['api']?.setDmsInstance?.(this.dmsService.dmsApi);
        }
      };
    } else if (app.routingPath === 'search') {
      return {
        addItemsToShoppingBasket: (event: CustomEvent<PairSearchAddEvent>): void => {
          if (event.detail.flatRateUnits?.length > 0) {
            const flatRateUnits: IFlatRateUnit[] = event.detail.flatRateUnits.map((flatRateUnit: PairSearchFlatRateUnit): IFlatRateUnit => this.mapFlatRateUnitFromPairSearch(
              flatRateUnit));
            this.serviceCaseUpdater.addFlatRateUnits(flatRateUnits, 'search');
          }
          if (event.detail.packages?.length > 0) {
            const packages: IPackage[] = event.detail.packages
              .map((servicePackage: PairSearchPackage): IPackage => this.mapPackageFromPairSearch(servicePackage));
            this.serviceCaseUpdater.addPackages(packages, 'search');
          }
          if (event.detail.parts?.length > 0) {
            const parts: IPart[] = event.detail.parts
              .map((part: PairSearchPart): IPart => this.mapPartFromPairSearch(part));
            this.serviceCaseUpdater.addParts(parts, 'search');
          }
          if (event.detail.technicalDocument) {
            const technicalDocument: ITechnicalDocument = this.mapTechnicalDocumentFromPairSearch(event.detail.technicalDocument);
            this.serviceCaseUpdater.addTechnicalDocument(technicalDocument, 'search');
          }
        },
        // set dmsApi on PAIR search once it is ready
        ready: (event: Event): void => {
          event.target['api']?.setDmsInstance?.(this.dmsService.dmsApi);
        }
      };
    } else {
      return {};
    }
  }

  private mapPartFromPartsCatalog(input: PartsCatalogPart): IPart {
    const part = new Part(
      input.partNumber.slice(0, 2),
      input.partNumber.slice(2, 4),
      input.partNumber.slice(4),
      input.partDescription,
      input.quantity
    );
    part.setClearingFlag(input.isTcRelevant ?? false);
    part.setAvailabilityNote(input.availabilityNote || null);

    return part;
  }

  private mapFlatRateUnitFromPairSearch(value: PairSearchFlatRateUnit): IFlatRateUnit {
    const flatRateUnit = new FlatRateUnit(value.number, value.description, value.amount, value.type, value.unit);
    flatRateUnit.setComment(value.azComment?.trim() || undefined);
    flatRateUnit.setGrossPrice(value.grossPrice);

    return flatRateUnit;
  }

  private mapPackageFromPairSearch(value: PairSearchPackage): IPackage {
    const flatRateUnits: IFlatRateUnit[] = value.flatRateUnits
      .map((fru: PairSearchFlatRateUnit): IFlatRateUnit => this.mapFlatRateUnitFromPairSearch(fru));
    const parts: IPart[] = value.parts.map((part: PairSearchPart): IPart => this.mapPartFromPairSearch(part));
    const defects: IDefect[] = value.defects.map((defect: any): IDefect => this.mapDefectFromPairSearch(defect));

    const servicePackage = new Package(value.number, value.description, value.type);
    servicePackage.setDefects(defects);
    servicePackage.setFlatRateUnits(flatRateUnits);
    servicePackage.setParts(parts);
    servicePackage.setGrossPrice(value.grossPrice);

    if (value.campaign) {
      servicePackage.setCampaignNumber(value.campaign.number);
      servicePackage.setCampaignName(value.campaign.name);
      servicePackage.setCampaignBegin(new Date(value.campaign.start));
      servicePackage.setCampaignEnd(new Date(value.campaign.end));
    }

    return servicePackage;
  }

  private mapPartFromPairSearch(input: PairSearchPart): IPart {
    const part = new Part(
      input.number.slice(0, 2),
      input.number.slice(2, 4),
      input.number.slice(4),
      input.description,
      input.quantity
    );
    part.setClearingFlag(input.isTcRelevant ?? false);
    part.setAvailabilityNote(input.availabilityNote || null);
    part.setTotalCustomerGrossPrice(input.totalCustomerGrossPrice);

    return part;
  }

  private mapDefectFromPairSearch(input: PairSearchDefect): IDefect {
    return new Defect(input.code, input.description, input.type);
  }

  private mapTechnicalDocumentFromPairSearch(input: PairSearchTechnicalDocument): ITechnicalDocument {
    // PAIR search (supplier) ID equals the source (publication) ID
    let technicalDocument: ITechnicalDocument = new TechnicalDocument(input.id, input.id, input.title, input.type);
    technicalDocument.setSubType(input.subType);
    return technicalDocument;
  }

  private mapFlatRateUnitFromFlatRateUnitSearch(value: FlatRateUnitSearchFlatRateUnit): IFlatRateUnit {
    let type: string = '';
    switch (value.displayType) {
      case 'DEFAULT':
        type = 'AW';
        break;
      case 'ARBEITSZEIT':
        type = 'AZ';
        break;
    }
    const flatRateUnit = new FlatRateUnit(value.number, value.text, value.value, type, type);
    flatRateUnit.setComment(value.userComment?.trim() || undefined);

    return flatRateUnit;
  }
}
